import React from 'react';
import { Plus } from 'lucide-react';
import { ReactComponent as DataCollectEmptyIllustration } from '../../../../assets/svg/dataCollect-empty-state.svg';
import { priceListFileAccept } from '../../../../services/materialCommerceSheetService.ts';
import type { CommerceMaterialSingleItemCreateInput } from '../../../../services/materialCommerceSheetService.ts';
import listaPickStyles from '../../../../pages/Comercios/CommerceAltaPage.module.scss';

export interface CommerceFormCatalogSectionProps {
  styles: Record<string, string>;
  listaPickStyles: Record<string, string>;
  suppressCatalogBlocks: boolean;
  materialPrecargaGateApplies: boolean;
  flexibleSheetEnabled: boolean;
  showPriceSheetBlock: boolean;
  disabled: boolean;
  priceSheet: File | null;
  priceSheetError: string;
  isDragOverSheet: boolean;
  sheetInputRef: React.RefObject<HTMLInputElement>;
  pickSheetButtonRef: React.RefObject<HTMLButtonElement>;
  onSheetDragEnter: (e: React.DragEvent) => void;
  onSheetDragLeave: (e: React.DragEvent) => void;
  onSheetDragOver: (e: React.DragEvent) => void;
  onSheetDrop: (e: React.DragEvent) => void;
  onHiddenSheetChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  applyPriceSheetFile: (file: File | null) => void;
  pendingCatalogMaterials: CommerceMaterialSingleItemCreateInput[];
  setPendingCatalogMaterials: React.Dispatch<React.SetStateAction<CommerceMaterialSingleItemCreateInput[]>>;
  onOpenPrecargaModal: () => void;
}

const CommerceFormCatalogSection: React.FC<CommerceFormCatalogSectionProps> = ({
  styles,
  listaPickStyles,
  suppressCatalogBlocks,
  materialPrecargaGateApplies,
  flexibleSheetEnabled,
  showPriceSheetBlock,
  disabled,
  priceSheet,
  priceSheetError,
  isDragOverSheet,
  sheetInputRef,
  pickSheetButtonRef,
  onSheetDragEnter,
  onSheetDragLeave,
  onSheetDragOver,
  onSheetDrop,
  onHiddenSheetChange,
  applyPriceSheetFile,
  pendingCatalogMaterials,
  setPendingCatalogMaterials,
  onOpenPrecargaModal,
}) => {
  if (suppressCatalogBlocks) return null;

  if (materialPrecargaGateApplies) {
    return (
      <section className={styles.catalogLoadUnified} aria-labelledby="commerce-catalog-heading">
        <h2 id="commerce-catalog-heading" className={styles.sheetTitle}>
          Listado de materiales
        </h2>
        <p className={styles.catalogLoadIntro}>
          {flexibleSheetEnabled
            ? 'Podés cargar tu oferta con un archivo de precios y productos o sumando materiales del catálogo Edify de a uno (también podés combinar ambos). Hace falta al menos uno de estos caminos para registrar el comercio.'
            : 'Con tu cuenta el listado se arma material por material desde el catálogo Edify; agregá al menos uno antes de registrar el comercio. La planilla flexible por archivo está disponible solo para tipo cliente.'}
        </p>

        {flexibleSheetEnabled ? (
          <div className={styles.catalogLoadRow}>
            <div className={`${styles.catalogLoadCol} ${styles.catalogLoadColFile}`}>
              <h3 id="commerce-catalog-file" className={styles.catalogLoadSubheading}>
                Por archivo
              </h3>
              <p className={styles.catalogLoadHint}>
                PDF, imagen, texto, JSON, CSV o Excel. Lo interpretamos y después revisás la tabla antes de publicar.
              </p>
              <input
                ref={sheetInputRef}
                type="file"
                accept={priceListFileAccept}
                className={styles.sheetInputHidden}
                disabled={disabled}
                onChange={onHiddenSheetChange}
                aria-labelledby="commerce-catalog-file"
              />
              <div
                className={`${styles.pickDropZone} ${styles.catalogLoadPickZone} ${!disabled && isDragOverSheet ? styles.pickDropZoneDragOver : ''}`}
                {...(!disabled && flexibleSheetEnabled
                  ? {
                      onDragEnter: onSheetDragEnter,
                      onDragLeave: onSheetDragLeave,
                      onDragOver: onSheetDragOver,
                      onDrop: onSheetDrop,
                    }
                  : {})}
              >
                {priceSheet ? (
                  <div className={listaPickStyles.commerceListaPickSplit}>
                    <div className={listaPickStyles.commerceListaPickSplitArt}>
                      <DataCollectEmptyIllustration
                        className={listaPickStyles.commerceListaPickEmptySvg}
                        aria-hidden
                      />
                    </div>
                    <div className={listaPickStyles.commerceListaPickSplitMain}>
                      <p className={`${styles.pickDropHint} ${listaPickStyles.commerceListaPickSplitHint}`}>
                        Seguís con <strong>{priceSheet.name}</strong>. Podés elegir otro archivo (arrastrá acá o usá
                        &quot;Cambiar archivo&quot;) antes de guardar el comercio.
                      </p>
                      <div className={listaPickStyles.commerceListaPickActionRow}>
                        <button
                          type="button"
                          className={styles.pickPrimaryBtn}
                          disabled={disabled}
                          onClick={() => sheetInputRef.current?.click()}
                        >
                          Cambiar archivo
                        </button>
                        <button
                          type="button"
                          className={styles.cancel}
                          disabled={disabled}
                          onClick={() => applyPriceSheetFile(null)}
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={listaPickStyles.commerceListaPickSplit}>
                    <div className={listaPickStyles.commerceListaPickSplitArt}>
                      <DataCollectEmptyIllustration
                        className={listaPickStyles.commerceListaPickEmptySvg}
                        aria-hidden
                      />
                    </div>
                    <div className={listaPickStyles.commerceListaPickSplitMain}>
                      <p className={`${styles.pickDropHint} ${listaPickStyles.commerceListaPickSplitHint}`}>
                        Arrastrá y soltá acá o usá &quot;Elegir archivo&quot;.
                      </p>
                      <button
                        ref={pickSheetButtonRef}
                        type="button"
                        className={styles.pickPrimaryBtn}
                        disabled={disabled}
                        onClick={() => sheetInputRef.current?.click()}
                      >
                        Elegir archivo
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {priceSheetError ? <p className={styles.sheetFileError}>{priceSheetError}</p> : null}
            </div>
            <div className={styles.catalogLoadDividerVertical} aria-hidden="true">
              <span className={styles.catalogLoadDividerVerticalLine} />
              <span className={styles.catalogLoadDividerVerticalText}>o bien</span>
              <span className={styles.catalogLoadDividerVerticalLine} />
            </div>
            <div className={`${styles.catalogLoadCol} ${styles.catalogLoadColCatalog}`}>
              <h3 className={styles.catalogLoadSubheading}>De a uno desde el catálogo</h3>
              <p className={styles.catalogLoadHint}>
                Buscá el material en el catálogo, completá importe u opciones; los cambios definitivos son al crear el
                comercio.
              </p>
              <div className={styles.precargaActions}>
                <button
                  type="button"
                  className={`${styles.pickPrimaryBtn} ${styles.precargaPrimaryBtn}`}
                  disabled={disabled}
                  onClick={onOpenPrecargaModal}
                >
                  <Plus size={18} aria-hidden strokeWidth={2} className={styles.precargaBtnIcon} />
                  Agregar desde catálogo
                </button>
              </div>
              {pendingCatalogMaterials.length > 0 ? (
                <ul className={styles.precargaChipList} aria-label="Materiales agregados">
                  {pendingCatalogMaterials.map((m, idx) => (
                    <li key={`${m.materialId}-${idx}`} className={styles.precargaChip}>
                      <span className={styles.precargaChipText}>{m.rawName}</span>
                      <button
                        type="button"
                        className={styles.precargaChipRemove}
                        disabled={disabled}
                        aria-label={`Quitar ${m.rawName}`}
                        onClick={() => setPendingCatalogMaterials((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <p className={`${styles.sheetJobboardNote} ${styles.catalogLoadJobNote}`}>
              Con tu tipo de cuenta la carga por planilla archivo está disponible para cuentas cliente; utilizá la
              opción siguiente para armar tu listado ahora mismo.
            </p>
            <div className={styles.catalogLoadStandalone}>
              <h3 className={styles.catalogLoadSubheading}>De a uno desde el catálogo</h3>
              <p className={styles.catalogLoadHint}>
                Buscá el material en el catálogo, completá importe u opciones; los cambios definitivos son al crear el
                comercio.
              </p>
              <div className={styles.precargaActions}>
                <button
                  type="button"
                  className={`${styles.pickPrimaryBtn} ${styles.precargaPrimaryBtn}`}
                  disabled={disabled}
                  onClick={onOpenPrecargaModal}
                >
                  <Plus size={18} aria-hidden strokeWidth={2} className={styles.precargaBtnIcon} />
                  Agregar desde catálogo
                </button>
              </div>
              {pendingCatalogMaterials.length > 0 ? (
                <ul className={styles.precargaChipList} aria-label="Materiales agregados">
                  {pendingCatalogMaterials.map((m, idx) => (
                    <li key={`${m.materialId}-${idx}`} className={styles.precargaChip}>
                      <span className={styles.precargaChipText}>{m.rawName}</span>
                      <button
                        type="button"
                        className={styles.precargaChipRemove}
                        disabled={disabled}
                        aria-label={`Quitar ${m.rawName}`}
                        onClick={() => setPendingCatalogMaterials((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </>
        )}
      </section>
    );
  }

  if (showPriceSheetBlock && flexibleSheetEnabled) {
    return (
      <section className={styles.sheetSection} aria-labelledby="commerce-sheet-heading">
        <h2 id="commerce-sheet-heading" className={styles.sheetTitle}>
          Archivo
        </h2>
        <input
          ref={sheetInputRef}
          type="file"
          accept={priceListFileAccept}
          className={styles.sheetInputHidden}
          disabled={disabled}
          onChange={onHiddenSheetChange}
        />
        <div
          className={`${styles.pickDropZone} ${!disabled && isDragOverSheet ? styles.pickDropZoneDragOver : ''}`}
          {...(!disabled && flexibleSheetEnabled
            ? {
                onDragEnter: onSheetDragEnter,
                onDragLeave: onSheetDragLeave,
                onDragOver: onSheetDragOver,
                onDrop: onSheetDrop,
              }
            : {})}
        >
          {priceSheet ? (
            <div className={listaPickStyles.commerceListaPickSplit}>
              <div className={listaPickStyles.commerceListaPickSplitArt}>
                <DataCollectEmptyIllustration className={listaPickStyles.commerceListaPickEmptySvg} aria-hidden />
              </div>
              <div className={listaPickStyles.commerceListaPickSplitMain}>
                <p className={`${styles.pickDropHint} ${listaPickStyles.commerceListaPickSplitHint}`}>
                  Seguís con <strong>{priceSheet.name}</strong>. Podés elegir otro archivo (arrastrá acá o usá
                  &quot;Cambiar archivo&quot;) antes de guardar el comercio.
                </p>
                <div className={listaPickStyles.commerceListaPickActionRow}>
                  <button
                    type="button"
                    className={styles.pickPrimaryBtn}
                    disabled={disabled}
                    onClick={() => sheetInputRef.current?.click()}
                  >
                    Cambiar archivo
                  </button>
                  <button
                    type="button"
                    className={styles.cancel}
                    disabled={disabled}
                    onClick={() => applyPriceSheetFile(null)}
                  >
                    Quitar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className={listaPickStyles.commerceListaPickSplit}>
              <div className={listaPickStyles.commerceListaPickSplitArt}>
                <DataCollectEmptyIllustration className={listaPickStyles.commerceListaPickEmptySvg} aria-hidden />
              </div>
              <div className={listaPickStyles.commerceListaPickSplitMain}>
                <p className={`${styles.pickDropHint} ${listaPickStyles.commerceListaPickSplitHint}`}>
                  Arrastrá y soltá acá o usá &quot;Elegir archivo&quot;. Formatos: PDF, imagen, CSV o Excel. Con un
                  archivo válido, la IA de Edify lo interpreta para armar la tabla de precios; puede tardar un poco y
                  después revisás todo antes de publicar.
                </p>
                <button
                  ref={pickSheetButtonRef}
                  type="button"
                  className={styles.pickPrimaryBtn}
                  disabled={disabled}
                  onClick={() => sheetInputRef.current?.click()}
                >
                  Elegir archivo
                </button>
              </div>
            </div>
          )}
        </div>
        {priceSheetError ? <p className={styles.sheetFileError}>{priceSheetError}</p> : null}
      </section>
    );
  }

  if (showPriceSheetBlock) {
    return (
      <p className={styles.sheetJobboardNote}>
        Con tu tipo de cuenta podés registrar el comercio; la carga de planilla flexible de precios está disponible
        para cuentas cliente. Podés completar el listado más adelante desde las herramientas de comercio cuando
        corresponda.
      </p>
    );
  }

  return null;
};

export default CommerceFormCatalogSection;
